import React, { useState, useEffect } from 'react';
import { 
  Facebook, Twitter, Instagram, Linkedin, Youtube, BookText as TikTok, 
  RefreshCw, Settings, Shield, Check, AlertCircle, X, Link2, 
  Radio, Globe, MapPin, Cloud, Camera, PinIcon, Share
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/Button';

interface SocialProvider {
  id: string;
  name: string;
  icon: React.ReactNode;
  connected: boolean;
  lastSync?: Date;
  accountName?: string;
  status: 'active' | 'pending' | 'error' | 'disconnected';
}

interface SocialSettings {
  autoPost: boolean;
  approvalRequired: boolean;
  defaultScheduleTime: string;
  postFrequency: 'daily' | 'weekly' | 'custom';
  customSchedule?: {
    days: number[];
    times: string[];
  };
  contentPreferences: {
    includeImages: boolean;
    maxPostLength: number;
    hashtagStrategy: 'auto' | 'manual' | 'both';
    defaultHashtags: string[];
  };
}

const SocialMediaIntegration: React.FC = () => {
  const { user } = useAuth();
  const [providers, setProviders] = useState<SocialProvider[]>([
    {
      id: 'facebook',
      name: 'Facebook',
      icon: <Facebook className="h-6 w-6 text-blue-600" />,
      connected: false,
      status: 'disconnected'
    },
    {
      id: 'twitter',
      name: 'Twitter/X',
      icon: <Twitter className="h-6 w-6 text-black" />,
      connected: false,
      status: 'disconnected'
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: <Instagram className="h-6 w-6 text-pink-600" />,
      connected: false,
      status: 'disconnected'
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: <Linkedin className="h-6 w-6 text-blue-700" />,
      connected: false,
      status: 'disconnected'
    },
    {
      id: 'youtube',
      name: 'YouTube',
      icon: <Youtube className="h-6 w-6 text-red-600" />,
      connected: false,
      status: 'disconnected'
    },
    {
      id: 'tiktok',
      name: 'TikTok',
      icon: <TikTok className="h-6 w-6 text-black" />,
      connected: false,
      status: 'disconnected'
    },
    {
      id: 'rumble',
      name: 'Rumble',
      icon: <Radio className="h-6 w-6 text-green-600" />,
      connected: false,
      status: 'disconnected'
    },
    {
      id: 'truthsocial',
      name: 'Truth Social',
      icon: <Share className="h-6 w-6 text-blue-500" />,
      connected: false,
      status: 'disconnected'
    },
    {
      id: 'bluesky',
      name: 'Bluesky',
      icon: <Cloud className="h-6 w-6 text-blue-400" />,
      connected: false,
      status: 'disconnected'
    },
    {
      id: 'googlebusiness',
      name: 'Google Business',
      icon: <MapPin className="h-6 w-6 text-red-500" />,
      connected: false,
      status: 'disconnected'
    },
    {
      id: 'mastodon',
      name: 'Mastodon',
      icon: <Globe className="h-6 w-6 text-purple-600" />,
      connected: false,
      status: 'disconnected'
    },
    {
      id: 'pinterest',
      name: 'Pinterest',
      icon: <PinIcon className="h-6 w-6 text-red-600" />,
      connected: false,
      status: 'disconnected'
    },
    {
      id: 'threads',
      name: 'Threads',
      icon: <Camera className="h-6 w-6 text-black" />,
      connected: false,
      status: 'disconnected'
    }
  ]);

  const [socialSettings, setSocialSettings] = useState<SocialSettings>({
    autoPost: false,
    approvalRequired: true,
    defaultScheduleTime: '09:00',
    postFrequency: 'weekly',
    contentPreferences: {
      includeImages: true,
      maxPostLength: 280,
      hashtagStrategy: 'both',
      defaultHashtags: ['FinancialPlanning', 'RetirementPlanning', 'WealthManagement']
    }
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState<string | null>(null);
  const [showConfigModal, setShowConfigModal] = useState<string | null>(null);

  // Load saved credentials on component mount
  useEffect(() => {
    if (user) {
      loadSocialCredentials();
    }
  }, [user]);

  const loadSocialCredentials = async () => {
    if (!user) return;
    
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase
        .from('user_credentials')
        .select('provider, credentials')
        .eq('user_id', user.id)
        .in('provider', [
          'facebook', 'twitter', 'instagram', 'linkedin', 'youtube', 'tiktok',
          'rumble', 'truthsocial', 'bluesky', 'googlebusiness', 'mastodon', 'pinterest', 'threads'
        ]);
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        // Update providers with connection status
        const updatedProviders = [...providers];
        
        data.forEach(credential => {
          const providerIndex = updatedProviders.findIndex(p => p.id === credential.provider);
          
          if (providerIndex !== -1) {
            updatedProviders[providerIndex] = {
              ...updatedProviders[providerIndex],
              connected: true,
              status: 'active',
              lastSync: new Date(credential.credentials.lastSync || Date.now()),
              accountName: credential.credentials.accountName || 'Connected Account'
            };
          }
        });
        
        setProviders(updatedProviders);
      }
    } catch (error) {
      console.error('Error loading social credentials:', error);
      toast.error('Failed to load social media connections');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async (providerId: string) => {
    if (!user) {
      toast.error('You must be logged in to connect social accounts');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // In a real implementation, this would initiate OAuth flow
      // For this demo, we'll simulate a successful connection
      
      // Simulate OAuth flow delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Store the credentials in the database
      const { error } = await supabase
        .from('user_credentials')
        .upsert({
          user_id: user.id,
          provider: providerId,
          credentials: {
            access_token: 'mock_access_token',
            refresh_token: 'mock_refresh_token',
            expiry: new Date(Date.now() + 3600000).toISOString(),
            accountName: `${providerId.charAt(0).toUpperCase() + providerId.slice(1)} Account`,
            lastSync: new Date().toISOString()
          }
        });
      
      if (error) throw error;
      
      // Update the provider in state
      setProviders(prev => prev.map(provider => {
        if (provider.id === providerId) {
          return {
            ...provider,
            connected: true,
            status: 'active',
            lastSync: new Date(),
            accountName: `${providerId.charAt(0).toUpperCase() + providerId.slice(1)} Account`
          };
        }
        return provider;
      }));
      
      toast.success(`Connected to ${providerId} successfully`);
    } catch (error) {
      console.error(`Failed to connect to ${providerId}:`, error);
      toast.error(`Failed to connect to ${providerId}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async (providerId: string) => {
    if (!user) return;
    
    setIsLoading(true);
    
    try {
      // Delete the credentials from the database
      const { error } = await supabase
        .from('user_credentials')
        .delete()
        .eq('user_id', user.id)
        .eq('provider', providerId);
      
      if (error) throw error;
      
      // Update the provider in state
      setProviders(prev => prev.map(provider => {
        if (provider.id === providerId) {
          return {
            ...provider,
            connected: false,
            status: 'disconnected',
            lastSync: undefined,
            accountName: undefined
          };
        }
        return provider;
      }));
      
      toast.success(`Disconnected from ${providerId}`);
    } catch (error) {
      console.error(`Failed to disconnect from ${providerId}:`, error);
      toast.error(`Failed to disconnect from ${providerId}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async (providerId: string) => {
    try {
      const provider = providers.find(p => p.id === providerId);
      if (!provider?.connected) return;

      setIsSyncing(providerId);

      toast.promise(
        new Promise(resolve => setTimeout(resolve, 2000)),
        {
          loading: 'Syncing with ' + provider.name + '...',
          success: `Synced with ${provider.name} successfully`,
          error: `Failed to sync with ${provider.name}`
        }
      );

      setProviders(prev => prev.map(p => {
        if (p.id === providerId) {
          return { ...p, lastSync: new Date() };
        }
        return p;
      }));

      setTimeout(() => {
        setIsSyncing(null);
      }, 2000);
    } catch (error) {
      console.error('Sync error:', error);
      setIsSyncing(null);
    }
  };

  const handleSyncAll = async () => {
    try {
      setIsSyncing('all');

      toast.promise(
        new Promise(resolve => setTimeout(resolve, 3000)),
        {
          loading: 'Syncing all social media accounts...',
          success: 'All accounts synced successfully',
          error: 'Failed to sync some accounts'
        }
      );

      // Update last sync time for all connected providers
      setProviders(prev => prev.map(p => {
        if (p.connected) {
          return { ...p, lastSync: new Date() };
        }
        return p;
      }));

      setTimeout(() => {
        setIsSyncing(null);
      }, 3000);
    } catch (error) {
      console.error('Sync all error:', error);
      setIsSyncing(null);
    }
  };

  const handleSaveSettings = async () => {
    if (!user) return;
    
    setIsLoading(true);
    
    try {
      // In a real implementation, this would update settings in the database
      // For this demo, we'll just show a success message
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('Social media settings saved successfully');
    } catch (error) {
      console.error('Error saving social media settings:', error);
      toast.error('Failed to save social media settings');
    } finally {
      setIsLoading(false);
    }
  };

  const openConfigModal = (providerId: string) => {
    setShowConfigModal(providerId);
  };

  const closeConfigModal = () => {
    setShowConfigModal(null);
  };

  const getStatusBadge = (status: SocialProvider['status']) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <Check className="h-3 w-3 mr-1" />
          Active
        </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
          Pending
        </span>;
      case 'error':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertCircle className="h-3 w-3 mr-1" />
          Error
        </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <X className="h-3 w-3 mr-1" />
          Disconnected
        </span>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Social Media Integration</h2>
          <p className="mt-1 text-sm text-gray-500">
            Connect and manage your social media accounts for posting and scheduling
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Button
            onClick={handleSyncAll}
            className="py-1.5 px-3"
            disabled={isLoading || isSyncing !== null || !providers.some(p => p.connected)}
          >
            {isSyncing === 'all' ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Sync All
              </>
            )}
          </Button>
          
          <Button
            onClick={handleSaveSettings}
            className="py-1.5 px-3"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </div>
      </div>

      {/* Social Media Providers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {providers.map((provider) => (
          <div
            key={provider.id}
            className={`bg-white rounded-lg border p-6 ${
              provider.connected ? 'border-primary' : 'border-gray-200'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {provider.icon}
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-gray-900">{provider.name}</h3>
                    {provider.status && getStatusBadge(provider.status)}
                  </div>
                  {provider.connected && (
                    <div className="text-sm text-gray-500 mt-1">
                      <div>Account: {provider.accountName}</div>
                      {provider.lastSync && (
                        <div>Last synced: {provider.lastSync.toLocaleString()}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {provider.connected ? (
                  <>
                    <button
                      onClick={() => handleSync(provider.id)}
                      className="p-2 text-gray-400 hover:text-gray-500"
                      title="Sync now"
                      disabled={isSyncing !== null}
                    >
                      {isSyncing === provider.id ? (
                        <RefreshCw className="h-5 w-5 animate-spin text-primary" />
                      ) : (
                        <RefreshCw className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => openConfigModal(provider.id)}
                      className="btn btn-outline py-1.5 px-3"
                    >
                      Configure
                    </button>
                    <button
                      onClick={() => handleDisconnect(provider.id)}
                      className="btn btn-outline py-1.5 px-3"
                    >
                      Disconnect
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleConnect(provider.id)}
                    className="btn btn-primary py-1.5 px-3"
                    disabled={isLoading}
                  >
                    {isLoading && provider.id === 'facebook' ? 'Connecting...' : 'Connect'}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Global Social Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Global Social Media Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Posting Preferences</h4>
            <div className="space-y-4">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={socialSettings.autoPost}
                    onChange={(e) => setSocialSettings(prev => ({ ...prev, autoPost: e.target.checked }))}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable automatic posting</span>
                </label>
                <p className="mt-1 text-xs text-gray-500 ml-6">
                  When enabled, content will be automatically posted based on your schedule
                </p>
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={socialSettings.approvalRequired}
                    onChange={(e) => setSocialSettings(prev => ({ ...prev, approvalRequired: e.target.checked }))}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Require approval before posting</span>
                </label>
                <p className="mt-1 text-xs text-gray-500 ml-6">
                  When enabled, you'll need to approve all posts before they go live
                </p>
              </div>
              
              <div>
                <label className="block text-sm text-gray-700 mb-1">Default Posting Time</label>
                <input
                  type="time"
                  value={socialSettings.defaultScheduleTime}
                  onChange={(e) => setSocialSettings(prev => ({ ...prev, defaultScheduleTime: e.target.value }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-700 mb-1">Post Frequency</label>
                <select
                  value={socialSettings.postFrequency}
                  onChange={(e) => setSocialSettings(prev => ({ 
                    ...prev, 
                    postFrequency: e.target.value as 'daily' | 'weekly' | 'custom' 
                  }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="custom">Custom Schedule</option>
                </select>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Content Preferences</h4>
            <div className="space-y-4">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={socialSettings.contentPreferences.includeImages}
                    onChange={(e) => setSocialSettings(prev => ({ 
                      ...prev, 
                      contentPreferences: {
                        ...prev.contentPreferences,
                        includeImages: e.target.checked
                      }
                    }))}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Include images when available</span>
                </label>
              </div>
              
              <div>
                <label className="block text-sm text-gray-700 mb-1">Maximum Post Length</label>
                <input
                  type="number"
                  min="100"
                  max="2000"
                  value={socialSettings.contentPreferences.maxPostLength}
                  onChange={(e) => setSocialSettings(prev => ({ 
                    ...prev, 
                    contentPreferences: {
                      ...prev.contentPreferences,
                      maxPostLength: parseInt(e.target.value)
                    }
                  }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Character limit for posts (Twitter/X has a 280 character limit)
                </p>
              </div>
              
              <div>
                <label className="block text-sm text-gray-700 mb-1">Hashtag Strategy</label>
                <select
                  value={socialSettings.contentPreferences.hashtagStrategy}
                  onChange={(e) => setSocialSettings(prev => ({ 
                    ...prev, 
                    contentPreferences: {
                      ...prev.contentPreferences,
                      hashtagStrategy: e.target.value as 'auto' | 'manual' | 'both'
                    }
                  }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                >
                  <option value="auto">Auto-generate hashtags</option>
                  <option value="manual">Use only manual hashtags</option>
                  <option value="both">Combine auto and manual hashtags</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-gray-700 mb-1">Default Hashtags</label>
                <input
                  type="text"
                  value={socialSettings.contentPreferences.defaultHashtags.join(', ')}
                  onChange={(e) => setSocialSettings(prev => ({ 
                    ...prev, 
                    contentPreferences: {
                      ...prev.contentPreferences,
                      defaultHashtags: e.target.value.split(',').map(tag => tag.trim().replace(/^#/, ''))
                    }
                  }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  placeholder="FinancialPlanning, RetirementPlanning, etc."
                />
                <p className="mt-1 text-xs text-gray-500">
                  Comma-separated list of hashtags to include in posts
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Privacy Settings */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            Privacy Settings
          </h4>
          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={true}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-600">Encrypt social media tokens</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={true}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-600">Limit app permissions to minimum required</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={true}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-600">Log all social media activities</span>
            </label>
          </div>
        </div>
      </div>

      {/* Configuration Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Configure {providers.find(p => p.id === showConfigModal)?.name}
              </h3>
              <button
                onClick={closeConfigModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Account Information */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Account Information</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-4">
                    {providers.find(p => p.id === showConfigModal)?.icon}
                    <span className="ml-2 font-medium">
                      {providers.find(p => p.id === showConfigModal)?.accountName}
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Link2 className="h-4 w-4 mr-2" />
                    <span>Connected on {providers.find(p => p.id === showConfigModal)?.lastSync?.toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              {/* Platform-Specific Settings */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Platform Settings</h4>
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={true}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="ml-2 text-sm text-gray-700">Auto-post to this platform</span>
                    </label>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Post Format</label>
                    <select
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                    >
                      <option value="standard">Standard</option>
                      <option value="image">Image with Text</option>
                      <option value="link">Link with Preview</option>
                      <option value="video">Video Post</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Character Limit</label>
                    <input
                      type="number"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                      value={showConfigModal === 'twitter' ? 280 : 2000}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Maximum characters for {providers.find(p => p.id === showConfigModal)?.name} posts
                    </p>
                  </div>
                </div>
              </div>

              {/* Custom Schedule */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Custom Schedule</h4>
                <div className="space-y-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-gray-700">Use custom schedule for this platform</span>
                  </label>
                  
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Best Times to Post</label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Day</label>
                        <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm">
                          <option value="monday">Monday</option>
                          <option value="tuesday">Tuesday</option>
                          <option value="wednesday">Wednesday</option>
                          <option value="thursday">Thursday</option>
                          <option value="friday">Friday</option>
                          <option value="saturday">Saturday</option>
                          <option value="sunday">Sunday</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Time</label>
                        <input
                          type="time"
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                          defaultValue="09:00"
                        />
                      </div>
                    </div>
                    <button className="mt-2 text-sm text-primary hover:text-primary-dark">
                      + Add another time
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={closeConfigModal}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    toast.success('Configuration saved');
                    closeConfigModal();
                  }}
                >
                  Save Configuration
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Social Media Status */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Connection Status</h3>
        
        <div className="space-y-4">
          {providers.map((provider) => (
            <div key={provider.id} className="flex items-center justify-between border-b pb-3 last:border-b-0 last:pb-0">
              <div className="flex items-center">
                {provider.icon}
                <span className="ml-2 font-medium">{provider.name}</span>
              </div>
              <div className="flex items-center">
                {provider.connected ? (
                  <>
                    <Check className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-sm text-gray-600">
                      Connected as {provider.accountName}
                    </span>
                  </>
                ) : (
                  <>
                    <X className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-500">Not connected</span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SocialMediaIntegration;